// @gjl/voice — Fugue Lab pricing, codified.
//
// Single source of truth for every price string Fugue Lab shows a prospect:
// the pitch deck (`/deck` skill + `@gjl/pdf` template), and (as it adopts this
// module) the Studio marketing site `/packages` page.
//
// Two things live here:
//
//   1. FESTIVAL DECK TIERS — the live, validated festival go-to-market
//      (the full stack / Concierge / Project add-ons). This is what the pitch
//      deck renders today. `deckPricingForBudget()` reproduces the deck's
//      budget->highlight logic. The lead tier's price is DERIVED from the
//      catalog below, so it cannot drift from what the site quotes.
//
//   2. THE MODULAR CATALOG — the capability-priced model (website / email /
//      donor management / ticketing / applications / Music Library & Rehearsal
//      Manager, done-for-you services, add-ons, verticals). The Studio site
//      renders directly from it; it is the codified form of the strategy doc
//      `projects/brands/fugue-lab/docs/strategy/modular-pricing-and-studio-repositioning.md`.
//
// PRICING MODEL (as of 2026-07-29): capabilities are priced INDIVIDUALLY and a
// self-serve total is their sum. Two structures were removed that day and must
// not be reintroduced without a decision-log entry:
//   - the count-based self-serve bundle (1/$25, 2/$40, ...), which only worked
//     while every capability cost the same; and
//   - the $500/mo "Festival Platform" bundle, which existed only because
//     Applications had no standalone price. Festivals buy a la carte
//     (FESTIVAL_STACK_IDS / FESTIVAL_STACK_MONTHLY), with Concierge in
//     DFY_BUNDLES as the done-for-you option and deliberately no mid-tier.
//
// PURE module: data + string helpers only, no I/O. Safe to import from the
// React client, the deck skill script, and any Lambda alike.
//
// Voice: no em dashes anywhere in the copy strings (hard rule). Use "plus",
// commas, or hyphens for numeric ranges.
// FESTIVAL_DECK_TIERS lives in section 2, below CAPABILITIES — its lead tier's
// price is DERIVED from the capability catalog (see FESTIVAL_STACK_MONTHLY) and
// a const cannot read another const declared later in the module. Everything
// else about the deck stays here.
export const DECK_PRICING_NOTE = 'No per-applicant fees. No vendor lock-in. Month-to-month, or a rolled annual plan that matches your budget cycle.';
/** The budget above which Concierge is the highlighted tier. */
export const CONCIERGE_BUDGET_THRESHOLD = 1000;
/**
 * The canonical deck pricing block, with `highlight` set by the lead's monthly
 * budget: under the threshold (or an unknown-but-set low budget) highlights
 * Platform; at/above it highlights Concierge; an undefined budget highlights
 * nothing. Reproduces the `/deck` skill's original `pricingForBudget()`.
 */
export function deckPricingForBudget(budget) {
    const highlightConcierge = budget !== undefined && budget >= CONCIERGE_BUDGET_THRESHOLD;
    const highlightPlatform = budget !== undefined && !highlightConcierge;
    return {
        tiers: FESTIVAL_DECK_TIERS.map((t) => {
            if (t.id === 'platform')
                return { ...t, highlight: highlightPlatform };
            if (t.id === 'concierge')
                return { ...t, highlight: highlightConcierge };
            return { ...t };
        }),
        note: DECK_PRICING_NOTE,
    };
}
/** The platform capabilities, each priced individually. The flat "$25 for any
 *  capability, bundle by count" model was retired 2026-07-29: Website, Email,
 *  Ticketing, and the Music Library & Rehearsal Manager are $25/mo; Applications
 *  is $50/mo; Donor Management is $100/mo. Donations are free to *accept* (0%
 *  platform fee) — the paid capability is donor MANAGEMENT (recurring, receipts,
 *  CRM). Per-unit fees (ticketing) sit on top. A self-serve total is the sum of
 *  the real prices; see `selfServeMonthly`.
 *
 *  `selfServeLive` corrected 2026-08-03: Applications and the Music Library &
 *  Rehearsal Manager had been `false` since the field was introduced, but both
 *  have shipped admin shells and real orgs running themselves on them (Holy
 *  Trinity and GHC on Music Manager). The flag had gone stale in the safe
 *  direction, so nothing broke loudly, it just undersold two products on every
 *  pricing surface. Ticketing and Donor Management stay `false` and that is
 *  accurate: they are the Patron rail, which has never taken a live payment.
 *
 *  Note the corroboration in ./modules.ts: `PlatformModuleId` is exactly
 *  `website | applications | music`, the three capabilities with an admin shell
 *  a customer can log into. Email is self-serve without one. */
export const CAPABILITIES = [
    {
        id: 'website',
        name: 'Website',
        priceMonthly: 25,
        note: 'Hosting, editor, custom domain, forms, SEO.',
        selfServeLive: true,
    },
    {
        id: 'email',
        name: 'Email Marketing',
        priceMonthly: 25,
        note: 'Newsletters, sequences, automations. Up to 2,000 contacts; $35/mo up to 10,000. No send metering.',
        selfServeLive: true,
    },
    {
        id: 'donor-management',
        name: 'Donor Management',
        priceMonthly: 100,
        note: 'Recurring giving, automated tax receipts, campaigns, donor CRM. Accepting donations is free (0% platform fee); this is the management layer.',
        selfServeLive: false,
    },
    {
        id: 'ticketing',
        name: 'Ticketing',
        priceMonthly: 25,
        perUnitFee: '1.5% per ticket',
        note: 'Selling a ticket is free apart from the 1.5%; the monthly price is box-office management: tiers, capacity, QR check-in, reporting.',
        selfServeLive: false,
    },
    {
        id: 'applications',
        name: 'Applications',
        priceMonthly: 50,
        note: 'Festival and program applications with music-native fields, review workflows, and no per-applicant fees. Stands alone, or composes with website, email, and ticketing on one applicant record. Concierge runs the whole festival for you.',
        selfServeLive: true,
    },
    {
        id: 'music-manager',
        name: 'Music Library & Rehearsal Manager',
        priceMonthly: 25,
        note: 'Music library with performance history, rehearsal and service planning, and ensemble/roster management for any ensemble. Attendance, multi-ensemble, substitute management, and automations are included. The church-specific liturgy/lectionary layer is branded "Canticle". The ensemble-vertical anchor.',
        selfServeLive: true,
    },
];
/** Self-serve pricing is à la carte: a total is the sum of the real
 *  per-capability prices. The old flat "$25 for any capability, 2 for $40..."
 *  count-based bundle was retired 2026-07-29 when capabilities stopped being one
 *  price (Applications $50, Donor Management $100). The only remaining discount
 *  is annual prepay (2 months free); see ANNUAL_PREPAY_MONTHS_FREE. */
export function selfServeMonthly(ids) {
    return ids.reduce((sum, id) => {
        const cap = CAPABILITIES.find((c) => c.id === id);
        return cap ? sum + cap.priceMonthly : sum;
    }, 0);
}
/** "A, B, and C" / "A and B" / "A". */
function joinNames(names) {
    if (names.length <= 1)
        return names[0] ?? '';
    if (names.length === 2)
        return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}
/** Capability names grouped by the price they actually share, cheapest first.
 *
 *  Grouping is the point. The obvious implementation — derive ONE price and
 *  assert in prose that the others match it — was what this file did until
 *  2026-07-29, and it was only safe while every capability cost $25. Under a
 *  la carte it would keep rendering "Website, Email, Ticketing ... are $25 a
 *  month each" after Email moved to $30. Deriving the grouping too means a
 *  price change re-partitions the sentence instead of falsifying it. */
function catalogPriceClauses() {
    const byPrice = new Map();
    for (const cap of CAPABILITIES) {
        const names = byPrice.get(cap.priceMonthly) ?? [];
        // The long name needs an article to read as a noun phrase in a list.
        names.push(cap.id === 'music-manager' ? `the ${cap.name}` : cap.name);
        byPrice.set(cap.priceMonthly, names);
    }
    return [...byPrice.entries()]
        .sort(([a], [b]) => a - b)
        .map(([price, names], i) => {
        const subject = joinNames(names);
        const verb = names.length === 1 ? 'is' : 'are';
        // Only the first clause carries the unit; the rest inherit it.
        const unit = i === 0 ? (names.length === 1 ? ' a month' : ' a month each') : '';
        return `${subject} ${verb} $${price}${unit}`;
    })
        .join('; ');
}
/** Annual prepay: pay for 10 months, get 12 (about 17% off). Applies to every
 *  self-serve and done-for-you monthly plan. Per-unit/usage fees unaffected. */
export const ANNUAL_PREPAY_MONTHS_FREE = 2;
/** Months actually billed on an annual plan. */
export const ANNUAL_PREPAY_MONTHS_BILLED = 12 - ANNUAL_PREPAY_MONTHS_FREE;
/**
 * The yearly price for anything quoted per month.
 *
 * Exists so the discount is applied in ONE place. Stripe cannot compute this:
 * a yearly Price stores a flat `unit_amount` with no memory of the monthly rate
 * it came from, so "$25/mo" and "$250/yr" are two unrelated numbers to Stripe
 * and will silently disagree the first time one is repriced without the other.
 * Deriving the annual figure means a monthly change re-derives it instead.
 *
 * Returns whole dollars, matching `priceMonthly`. Every catalog price is a round
 * number of dollars, so ten times one is exact — no rounding decision is being
 * hidden here. The cents conversion belongs at the Stripe boundary, once.
 */
export function annualPrepayAmount(monthly) {
    return monthly * ANNUAL_PREPAY_MONTHS_BILLED;
}
/* ------------------------------------------------------------------ *
 * Who runs it: derived from `selfServeLive`, never restated in prose
 * ------------------------------------------------------------------ */
/** Capabilities the customer can run themselves today. */
export const SELF_SERVE_CAPABILITIES = CAPABILITIES.filter((c) => c.selfServeLive);
/** Capabilities our team sets up and runs today, included in the price. */
export const TEAM_RUN_CAPABILITIES = CAPABILITIES.filter((c) => !c.selfServeLive);
/** "Website, Email Marketing, and the Music Library & Rehearsal Manager".
 *  The long name needs an article to read as a noun phrase inside a list, the
 *  same correction `catalogPriceClauses` makes. */
export function capabilityNameList(caps) {
    return joinNames(caps.map((c) => (c.id === 'music-manager' ? `the ${c.name}` : c.name)));
}
/**
 * The self-serve / done-for-you split as one customer-facing sentence.
 *
 * Exists because that split was written out BY HAND in six places across four
 * files (the packages page twice, the pricing configurator, and two feature
 * pages), each naming the four then-done-for-you capabilities in prose. When
 * Applications and the Music Library & Rehearsal Manager went self-serve, every
 * one of those sentences became false at once, and two of them sat directly
 * above capability cards that correctly read "Run it yourself today" off the
 * flag. Adjacent contradicting claims on one page, with nothing to catch it.
 *
 * Same lesson as `catalogPriceClauses`, one field over: derive the sentence and
 * a status change rewrites it instead of falsifying it.
 *
 * Handles the end state where nothing is team-run, which is where this is going.
 */
export const SELF_SERVE_SPLIT_LINE = (() => {
    const selfServe = capabilityNameList(SELF_SERVE_CAPABILITIES);
    if (TEAM_RUN_CAPABILITIES.length === 0)
        return `Every capability is self-serve today.`;
    const teamRun = capabilityNameList(TEAM_RUN_CAPABILITIES);
    const teamVerb = TEAM_RUN_CAPABILITIES.length === 1 ? 'is' : 'are';
    const selfVerb = SELF_SERVE_CAPABILITIES.length === 1 ? 'is' : 'are';
    return (`${selfServe} ${selfVerb} self-serve today; ` +
        `${teamRun} ${teamVerb} set up and run for you by our team while their self-serve controls roll out.`);
})();
/** One line for the whole self-serve story, generated from the real prices AND
 *  their real groupings, so it cannot drift from the catalog.
 *
 *  The prepay clause used to hardcode "2 months free" while
 *  ANNUAL_PREPAY_MONTHS_FREE sat 400 lines below saying the same thing
 *  separately — the same shape of drift this function's grouping logic was
 *  written to prevent, one sentence later. It interpolates now.
 *
 *  Which is why the prepay constants are declared HERE rather than beside the
 *  other trailing exports: a top-level `const` is in its temporal dead zone
 *  until the line that declares it runs, so reading it from a template literal
 *  further up the file is a ReferenceError at import time, not a type error. */
export const CATALOG_RULE_LINE = `Turn on only what you need. ${catalogPriceClauses()}. Pay annually and get ${ANNUAL_PREPAY_MONTHS_FREE} months free.`;
/** The festival vertical's full stack. Single definition — VERTICAL_STACKS'
 *  'festival' entry and the deck's lead tier both read it, so the stack and its
 *  price can never disagree. */
export const FESTIVAL_STACK_IDS = ['website', 'applications', 'email', 'ticketing', 'donor-management'];
/** What a festival pays with every capability turned on. Replaced the flat
 *  $500/mo Festival Platform on 2026-07-29. */
export const FESTIVAL_STACK_MONTHLY = selfServeMonthly(FESTIVAL_STACK_IDS);
/** The canonical three festival tiers, no highlight applied. Lives here rather
 *  than in section 1 so the lead tier's price derives from CAPABILITIES. */
export const FESTIVAL_DECK_TIERS = [
    {
        id: 'platform',
        name: 'The full stack',
        price: `$${FESTIVAL_STACK_MONTHLY}/mo`,
        line: 'Website, applications, email, ticketing, and donor management, each priced on its own. Turn on only what you need. For teams with their own designer.',
    },
    {
        id: 'concierge',
        name: 'Concierge',
        price: '$1,000-1,500/mo',
        line: 'Every capability run for you, plus branding, design, and a done-for-you launch and content.',
    },
    {
        id: 'brand-projects',
        name: 'Brand projects',
        price: 'from $1,500',
        line: 'Brand direction, a full identity, print programs, photography, or a season campaign. One-time, any plan. Ladder in DESIGN_PROJECTS.',
    },
];
export const MANAGED_SERVICES = [
    {
        id: 'website-care',
        name: 'Website Care',
        priceMonthly: 100,
        note: 'One update request per month, 72-hour turnaround. Website software included.',
    },
    {
        id: 'managed-website',
        name: 'Fully Managed Website',
        priceMonthly: 500,
        note: 'Fully managed: send update requests any time, one active at a time, 72-hour turnaround.',
    },
    {
        id: 'managed-email',
        name: 'Managed Email',
        priceMonthly: 350,
        note: 'We plan, write, design, and send. About 2 campaigns per month plus 1 automation.',
    },
    {
        id: 'managed-design',
        name: 'Managed Design',
        priceMonthly: 750,
        note: 'A designer on call: social graphics, print pieces, campaign assets. One active request at a time, 72-hour turnaround. Includes upkeep of your brand system so every piece stays on-brand.',
    },
];
/** Managed Website is ONE service with two tiers (Care / Fully Managed), not two
 *  separate services. The customer-facing surfaces (nav, /packages, the
 *  done-for-you page) present it grouped; MANAGED_SERVICES stays flat so the
 *  pricing configurator and feature pages can still look each tier up by id.
 *  Tier prices are single-sourced from the MANAGED_SERVICES entries so they
 *  cannot drift. Both tiers include the website software and a 72-hour
 *  turnaround; the tier is only how many changes you send. */
export const MANAGED_WEBSITE = {
    id: 'managed-website',
    name: 'Managed Website',
    blurb: 'We keep your site current. Both tiers include the website software and a 72-hour turnaround; the tier is only how many changes you send.',
    tiers: [
        {
            id: 'website-care',
            label: 'Care',
            priceMonthly: MANAGED_SERVICES.find((s) => s.id === 'website-care').priceMonthly,
            note: 'One update request a month: a concert change, a new page, a season refresh.',
        },
        {
            id: 'managed-website',
            label: 'Fully Managed',
            priceMonthly: MANAGED_SERVICES.find((s) => s.id === 'managed-website').priceMonthly,
            note: 'Send update requests any time, worked one at a time.',
        },
    ],
};
/** The single-tier managed services (everything except the website tiers).
 *  Rendered alongside MANAGED_WEBSITE on the done-for-you surfaces so the two
 *  website tiers show as one card instead of two. */
export const OTHER_MANAGED_SERVICES = MANAGED_SERVICES.filter((s) => s.id !== 'website-care' && s.id !== 'managed-website');
export const DFY_BUNDLES = [
    {
        id: 'growth-partner',
        name: 'Growth Partner',
        price: '$800/mo',
        note: 'Fully Managed Website plus Managed Email, $50 under the two separately.',
    },
    {
        id: 'concierge',
        name: 'Concierge',
        price: '$1,000-1,500/mo',
        note: 'Everything done for you across all capabilities, plus strategy. Fundraising and box office run for you live here, and for festivals it covers Applications, branding, and launch.',
    },
];
// FESTIVAL_LANE (the $500/mo "Festival Platform" bundle) was REMOVED 2026-07-29.
// It existed because Applications had no standalone price; once Applications
// became a $50 capability, the same festival could see $225 a la carte and $500
// bundled on one page. Festivals now buy a la carte (website + applications +
// email + ticketing + donor management = the full stack) and Concierge in
// DFY_BUNDLES is the done-for-you option. There is deliberately no mid-tier.
// Festival Concierge had already merged into Concierge 2026-07-15.
export const CONSULTING = [
    {
        id: 'strategy-intensive',
        name: 'Digital Strategy Intensive',
        price: '$1,500-3,000 one-time',
        note: 'Audit plus roadmap plus capability setup.',
    },
    {
        id: 'fractional-director',
        name: 'Fractional Digital Director',
        price: '$500-1,500/mo',
        note: 'Ongoing strategy, oversight, prioritization.',
    },
];
/**
 * The brand ladder — arts organizations (festivals, orchestras, choruses,
 * ensembles, schools). Sold as one-time projects; Managed Design in
 * MANAGED_SERVICES is the recurring lane and Concierge includes this capacity.
 *
 * **The optimization target is (conversion x lifetime recurring), NOT one-time
 * yield.** This is the correction that set the numbers below, and it is easy to
 * get wrong twice.
 *
 * Design projects are, per business-model.md, "pre-Concierge tasters; one-off
 * rebrands" — the land-and-expand motion for a platform business whose path to
 * the $30K threshold is 2-11 customers at $225-1,500/mo. A project that scares a
 * prospect off costs the recurring line forever: at the $225/mo festival stack
 * it takes ~98 months of platform revenue to make back one lost close, where the
 * same arithmetic against the retired $500/mo tier said 44. Removing that tier
 * on 2026-07-29 made this argument STRONGER, not weaker — the recurring line a
 * scared-off prospect costs you is now smaller, so overpricing the one-time
 * project hurts more. Agency benchmarks ($10-30K
 * for a sub-$500K nonprofit) are correct for firms whose ONLY revenue is the
 * project, and are the wrong target when the project is the on-ramp.
 *
 * **First pass (2026-07-24) overshot on exactly that point** — a $2,500 sprint
 * rising to a $22,000 top rung, priced off agency comparables. Corrected the
 * same day. Historically realized here: builds $2-5K, project add-ons $1-3K.
 *
 * **But do not revert to the pre-2026-07-24 prices either.** Logo $750 /
 * package $1,500-3,000 was genuinely too low: it read as student work to an
 * organization with a $1M budget, and it sold Danielle's hours — the company's
 * scarcest input — at the worst rate on the price list. Both failure modes are
 * real; these numbers sit deliberately between them.
 *
 * Rung spacing (3.0x / 1.9x / 1.5x): the first jump crosses a real discipline
 * boundary (strategy -> design), where observed studio tier pages put 2.3-3.5x.
 * The later jumps deliberately UNDER-price the same rule (which would say
 * 2.3-3.5x for crossing into web and photography) because the recurring attach,
 * not the project fee, is what we are actually selling. Every rung names its
 * attach for that reason.
 *
 * PARISH_DESIGN_PROJECTS is the separate church lane, and now tiers sensibly
 * just below the arts entry rung. Do not collapse the two.
 */
export const DESIGN_PROJECTS = [
    {
        id: 'brand-sprint',
        name: 'Brand Direction Sprint',
        price: '$1,500 one-time',
        note: 'Two weeks. A working session that ends with a decision, a look at how you sit against comparable organizations, a written direction, and one finished piece: a season poster using your real repertoire. Credited in full against any brand project booked within 60 days.',
    },
    {
        id: 'identity-core',
        name: 'Identity Core',
        price: '$4,500 one-time',
        note: 'The mark and its variants, palette, typography, and a usage guide. Delivered live: your colors and type are applied to your running site, program PDFs, and tickets, not handed over as a document for someone else to implement. Pairs with the platform from $50 a month.',
    },
    {
        id: 'identity-system',
        name: 'Identity System',
        price: '$8,500 one-time',
        note: 'Identity Core plus how you sound and what you print: voice and boilerplate, concert program and ticket templates, email templates, and a social template set your staff can actually post from. Pairs with the platform plus Managed Design.',
    },
    {
        id: 'season-launch',
        name: 'Season Launch',
        price: 'From $12,500 one-time',
        note: 'The whole change, run for you: everything in Identity System plus a new site build, a season campaign, a photography day, and rollout support through the announcement. Quoted per season, and it usually continues as Concierge.',
    },
];
/**
 * Add-ons to any rung of the brand ladder. Priced against published
 * comparables rather than against our own hours.
 */
export const DESIGN_ADDONS = [
    {
        id: 'naming',
        name: 'Naming',
        price: '$2,500 one-time',
        note: 'A name, the reasoning behind it, and a check that it is actually available. Sold before identity work, never after.',
    },
    {
        id: 'verbal-identity',
        name: 'Verbal identity',
        price: '$2,500 one-time',
        note: 'How the organization sounds in writing: voice, boilerplate, and three message pillars, with before-and-after rewrites of your own copy.',
    },
    {
        id: 'season-campaign',
        name: 'Season campaign',
        price: '$3,500 one-time',
        note: 'Your whole season designed as one family: announcement graphics, program covers, social templates, and print pieces.',
    },
    {
        id: 'print-design',
        name: 'Print design',
        price: '$650-1,500 per piece',
        note: 'Concert programs, posters, postcards, season brochures. Print-ready files plus a web version of each.',
    },
    {
        id: 'motion-mark',
        name: 'Animated logo',
        price: '$1,200 one-time',
        note: 'Your mark in motion for reels, pre-concert screens, and video titles. Delivered with a transparent version so it sits on any footage.',
    },
];
/**
 * The parish / church lane — deliberately priced below the arts ladder.
 *
 * This is NOT an oversight and must not be "corrected" to match
 * DESIGN_PROJECTS. Church price expectation in this market is set by
 * denominational vendors who sell parish logos and brand books at "a few
 * thousand dollars," and the median US church income is around $165,000 with no
 * marketing line in the budget at all. Against that, this pricing is already at
 * market — the underpricing problem was specific to arts organizations.
 *
 * After the 2026-07-24 arts-ladder correction these two lanes overlap by design
 * rather than sitting an order of magnitude apart: a parish identity package
 * ($1,500-3,000) now runs just under Identity Core ($4,500), which is the right
 * relationship. A well-funded parish and a small festival are genuinely adjacent
 * buyers; a $1M orchestra is not.
 *
 * The practical route to a yes is also different: parish music work is
 * authorized by the Music Director out of the music budget, which needs no
 * vestry or finance-committee approval, so scope it to the concert series and
 * the choir season rather than to parish-wide identity.
 */
export const PARISH_DESIGN_PROJECTS = [
    {
        id: 'parish-logo',
        name: 'Logo design',
        price: '$750 one-time',
        note: 'A mark that holds up on a bulletin, a concert program, and a website header. Delivered in every format you will be asked for.',
    },
    {
        id: 'parish-brand-package',
        name: 'Parish identity package',
        price: '$1,500-3,000 one-time',
        note: 'Logo plus palette, typography, and a short usage guide, including substitute fonts, so a volunteer can produce a bulletin that still looks like your parish.',
    },
    {
        id: 'parish-concert-series',
        name: 'Concert series identity',
        price: '$1,200-2,500 one-time',
        note: 'A look for the music program specifically: series poster, program covers, and social templates for the choir season.',
    },
];
/**
 * Photography, sold per session (on-site work, so no retainer lane).
 *
 * Repriced 2026-07-24 alongside the brand ladder. The old numbers ($300-500 for
 * headshots, $800-1,200 for a full day) were internally inconsistent with a
 * studio charging $12,500 for an identity system, and brand-attached
 * photography does not appear in the market below that band at all. Photography
 * also feeds the client's Media Library natively, which no competitor bundles.
 */
export const PHOTOGRAPHY_PROJECTS = [
    {
        id: 'photo-performance',
        name: 'Performance session',
        price: '$650-900 per session',
        note: 'Concert or rehearsal coverage with an edited, web-ready gallery: the images your website, press, and grant applications keep asking for.',
    },
    {
        id: 'photo-headshots',
        name: 'Headshot session',
        price: '$500-750 per session',
        note: 'Directors, soloists, or a whole roster, shot consistently so your people pages and programs stop mixing selfies with studio shots.',
    },
    {
        id: 'photo-season-day',
        name: 'Season photography day',
        price: '$1,800 one-time',
        note: 'A full day across rehearsal, venue, and staged shots to stock a year of posters, socials, and appeals. Lands straight in your media library.',
    },
];
/** Photography is on-site work; design ships anywhere. */
export const PHOTOGRAPHY_SERVICE_AREA = 'Sessions are Los Angeles area by default; travel beyond it is quoted per engagement.';
/** Who does the creative work; customer-facing credit line. */
export const CREATIVE_LEAD = {
    name: 'Danielle Dinstman',
    credit: 'Photography and design are led by Danielle Dinstman, a Los Angeles artist and designer (BFA Painting, Boston University) whose work spans photography, illustration, and brand identity, including Fugue Lab’s own.',
};
/** One-time builds and small recurring add-ons. */
export const ONE_TIME_AND_ADDONS = [
    {
        id: 'launch-build',
        name: 'Launch build',
        price: '$250 one-time',
        note: 'Custom-designed site plus basic integrations. DNS/domain connection included. Doubles as the done-for-you onboarding fee.',
    },
    {
        id: 'bespoke-build',
        // "Bespoke" is internal-only vocabulary (voice rule: never in customer
        // copy). Any customer-facing render of this entry must display a
        // different label, e.g. "Custom build".
        name: 'Bespoke build',
        price: 'Quoted',
        note: 'Fully custom design, complex integrations, festival/multi-program work.',
    },
    {
        id: 'managed-domain',
        name: 'Managed Domain',
        price: '$25/yr',
        note: 'We register plus host in Route 53 and handle renewals. Registrant is always the client, transferable on request. No lock-in.',
    },
    {
        id: 'professional-email',
        name: 'Professional Email',
        price: '$5/mo',
        note: 'name@theirorg forwards to their existing inbox plus send-as. Free with any build.',
    },
    {
        id: 'workspace-setup',
        name: 'Workspace mailbox setup',
        price: '$50-150 one-time',
        note: 'One-time Google Workspace setup. The client owns the account and pays Google directly.',
    },
    {
        id: 'migration-heavy',
        name: 'Data migration (heavy)',
        price: '$150-500 one-time',
        note: 'Large donor/member/history imports. Simple content migration is bundled into a build. Offboarding export is always free.',
    },
];
export const VERTICAL_STACKS = [
    {
        id: 'church',
        name: 'Churches / parishes',
        anchor: 'music-manager',
        stack: ['website', 'email', 'donor-management', 'music-manager'],
        note: 'Anchored by the Music Library & Rehearsal Manager with the Canticle liturgy layer. Professional Email is a common add-on.',
    },
    {
        id: 'festival',
        name: 'Festivals',
        anchor: 'applications',
        stack: FESTIVAL_STACK_IDS,
        note: `Anchored by Applications (auditions). Sold a la carte (the full stack is $${FESTIVAL_STACK_MONTHLY}/mo), with Concierge as the done-for-you option.`,
    },
];
/* ------------------------------------------------------------------ *
 * Typed lookups — how a rename becomes a compile error
 * ------------------------------------------------------------------ */
/**
 * Build an id-keyed accessor whose parameter is the LITERAL UNION of the ids
 * present in `items`, and whose return type is non-optional.
 *
 * This exists because of a real outage-shaped bug. Consumers used to look
 * entries up inline:
 *
 *   FESTIVAL_DECK_TIERS.find((t) => t.name === 'Project add-ons')!
 *
 * Renaming that tier on 2026-07-24 made the lookup resolve to `undefined`. The
 * `!` asserted otherwise, so it typechecked, built, and then threw at render —
 * caught only by the SSR prerender in the studio's DEPLOY pipeline, because the
 * PR gate runs `vite build` with no SSR. A display string was acting as a
 * primary key, and nothing checked it.
 *
 * With `as const satisfies`, the arrays keep their literal id types, so:
 *
 *   deckTier('brand-projects')   // ok
 *   deckTier('project-add-ons')  // COMPILE ERROR, at the rename, on that PR
 *
 * The union is derived from the data, so it cannot drift: add an entry and the
 * type widens on its own; rename one and every call site fails loudly and
 * immediately. That is strictly better than catching it at deploy, and it costs
 * nothing in CI — which is why we did NOT put the prerender in the PR gate.
 *
 * The throw is unreachable by construction. It is here so the return type can be
 * non-optional honestly, rather than via a `!` that asserts something the
 * compiler cannot verify.
 */
function byId(items, label) {
    return (id) => {
        const found = items.find((item) => item.id === id);
        if (!found)
            throw new Error(`@gjl/voice: no ${label} with id "${id}"`);
        return found;
    };
}
/** Prefer these over an inline `.find(...)!`. Each rejects an unknown id at compile time. */
export const capabilityById = byId(CAPABILITIES, 'capability');
export const deckTier = byId(FESTIVAL_DECK_TIERS, 'deck tier');
export const managedService = byId(MANAGED_SERVICES, 'managed service');
export const dfyBundle = byId(DFY_BUNDLES, 'done-for-you bundle');
// festivalLaneBundle removed 2026-07-29 with FESTIVAL_LANE — see the note there.
export const oneTimeAddOn = byId(ONE_TIME_AND_ADDONS, 'one-time add-on');
export const verticalStack = byId(VERTICAL_STACKS, 'vertical stack');
export const consultingBundle = byId(CONSULTING, 'consulting bundle');
// Added 2026-08-01: PhotographyPage's SEO description carried a literal
// "from $300 per session" — the pre-2026-07-24 price, stale by two repricings
// while the page body rendered the catalogue correctly. A meta description is
// still customer-facing copy, so it gets an accessor like everything else.
export const photographyProject = byId(PHOTOGRAPHY_PROJECTS, 'photography project');
/**
 * Capability by id, returning `undefined` when absent.
 *
 * Kept for the genuine runtime case: an id arriving from outside the codebase
 * (a URL parameter, stored config) where absence is data, not a typo. When the
 * id is a literal you typed, use {@link capabilityById} so a rename cannot
 * silently resolve to nothing.
 */
export function capability(id) {
    return CAPABILITIES.find((c) => c.id === id);
}
